import { Dispatch, FC, SetStateAction, useMemo } from 'react';
import { SearchIcon } from '@dynamika-shared/ui/icons';
import { Option } from '@utils/generateOptions';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { debounce } from 'lodash';
import { ModalityLabel } from '@dynamika-shared/types/imagingLabels';
import { NoDataSelect } from '@dynamika-shared/ui/NoDataSelect/NoDataSelect';
import {
  HiddenTag,
  SelectLabelsOptionRender,
} from '@components/SelectLabels/SelectLabelsOptionRender';
import { FlattenOptionData } from 'rc-select/lib/interface';
import { BaseOptionType } from 'rc-select/lib/Select';
import { transformModalityLabel } from '@components/SelectLabels/utils';

export const MAX_LABEL_LENGTH = 20;

type SelectLabelsProps = {
  ctLabels: ModalityLabel[];
  imagingLabels: ModalityLabel[];
  fetchItems: (name: string) => Promise<void> | void;
  searchValue: string;
  selectedItems: Option[];
  onSearch: (value: string) => void;
  setSelectedItems: Dispatch<SetStateAction<Option[]>>;
};

export const SelectLabels: FC<SelectLabelsProps> = ({
  ctLabels,
  imagingLabels,
  fetchItems,
  searchValue,
  selectedItems,
  setSelectedItems,
  onSearch,
}) => {
  const labelsOptions = useMemo(
    () => transformModalityLabel(imagingLabels, ctLabels),
    [ctLabels, imagingLabels]
  );

  const isItemSelected = (value: string) => {
    return selectedItems.some(item => item.value === value);
  };

  const onSelect = (value: string) => {
    const selectedOption = labelsOptions?.find(group =>
      group.options.some(opt => opt.value === value)
    );
    if (selectedOption) {
      const selectedItem = selectedOption.options.find(
        opt => opt.value === value
      );
      if (selectedItem) {
        setSelectedItems(prevState => [...prevState, selectedItem]);
      }
    }
  };

  const renderOption = (option: FlattenOptionData<BaseOptionType>) => {
    return (
      <SelectLabelsOptionRender
        option={option}
        searchValue={searchValue}
        isItemSelected={isItemSelected}
      />
    );
  };

  const hiddenTagRender = () => {
    return <HiddenTag />;
  };

  const onDeselect = (value: string) => {
    const itemIndex = selectedItems.findIndex(item => item.value === value);
    if (itemIndex !== -1) {
      setSelectedItems(prevState => {
        const updatedSelectedItems = [...prevState];
        updatedSelectedItems.splice(itemIndex, 1);
        return updatedSelectedItems;
      });
    }
  };

  const delayedSearch = useMemo(
    () =>
      debounce((value: string) => {
        fetchItems(value);
      }, 500),
    [fetchItems]
  );

  return (
    <Select
      data-testid={'select-labels'}
      label='Labels'
      mode='multiple'
      suffixIcon={<SearchIcon className='h-2 w-2' />}
      required
      options={labelsOptions}
      placeholder={<span>Search Labels</span>}
      menuItemSelectedIcon={null}
      tagRender={() => hiddenTagRender()}
      filterOption={false}
      searchValue={searchValue}
      notFoundContent={
        searchValue ? (
          <NoDataSelect
            text={searchValue}
            name={'label'}
            maxLength={MAX_LABEL_LENGTH}
          />
        ) : null
      }
      onSearch={value => {
        onSearch(value);
        delayedSearch(value);
      }}
      value={selectedItems}
      onSelect={onSelect}
      onDeselect={onDeselect}
      onBlur={() => onSearch('')}
      optionRender={option => renderOption(option)}
    />
  );
};
